// Frontmatter parsing here is a hand-rolled subset of YAML (no js-yaml/yaml dependency),
// because in practice Obsidian frontmatter is almost always one of: flat scalars
// (`key: value`), inline arrays (`key: [a, b]`), or block lists (`key:\n  - a\n  - b`).
// NOT supported: nested objects/maps, multi-line block scalars (| or >), anchors/aliases,
// tabs for indentation, multi-line arrays with wrapped lines. Any of these cause frontmatter
// to fail soft to `null` rather than throwing or producing a wrong/partial object.

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n|$)/;
const FENCED_CODE_RE = /```[\s\S]*?```/g;
const INLINE_CODE_RE = /`[^`\n]*`/g;
const EMBED_OR_WIKILINK_RE = /(!?)\[\[([^\]]+)\]\]/g;
const MARKDOWN_LINK_RE = /(?<!!)\[([^\]]+)\]\(([^)]+)\)/g;
const INLINE_TAG_RE = /(?<![\w#])#([A-Za-z0-9_\-/]+)/g;

function stripQuotes(value) {
  if (
    value.length >= 2 &&
    ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'")))
  ) {
    return value.slice(1, -1);
  }
  return value;
}

function parseFrontmatterBlock(text) {
  const lines = text.split(/\r?\n/);
  const result = {};
  try {
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (line.trim() === '') {
        i++;
        continue;
      }
      if (/^\s/.test(line)) {
        // Unexpected indentation at the top level (e.g. a block list with no owning key).
        throw new Error('unexpected indentation');
      }
      const kvMatch = line.match(/^([^:\s][^:]*):\s*(.*)$/);
      if (!kvMatch) {
        throw new Error('unparseable line');
      }
      const key = kvMatch[1].trim();
      const inlineValue = kvMatch[2].trim();

      if (inlineValue === '') {
        const items = [];
        let j = i + 1;
        while (j < lines.length) {
          const itemMatch = lines[j].match(/^\s*-\s*(.*)$/);
          if (!itemMatch) break;
          items.push(stripQuotes(itemMatch[1].trim()));
          j++;
        }
        result[key] = items.length > 0 ? items : '';
        i = j > i + 1 ? j : i + 1;
        continue;
      }

      if (inlineValue.startsWith('[') && inlineValue.endsWith(']')) {
        const inner = inlineValue.slice(1, -1);
        result[key] = inner
          .split(',')
          .map((part) => stripQuotes(part.trim()))
          .filter((part) => part !== '');
      } else {
        result[key] = stripQuotes(inlineValue);
      }
      i++;
    }
  } catch {
    return null;
  }
  return result;
}

function extractFrontmatter(rawText) {
  const match = rawText.match(FRONTMATTER_RE);
  if (!match) {
    return { frontmatter: null, body: rawText };
  }
  return { frontmatter: parseFrontmatterBlock(match[1]), body: rawText.slice(match[0].length) };
}

function toTagList(value) {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : String(value).split(/[,\s]+/);
  return items.map((item) => String(item).trim().replace(/^#/, '')).filter(Boolean);
}

function maskCodeRegions(text) {
  return text.replace(FENCED_CODE_RE, '').replace(INLINE_CODE_RE, '');
}

function splitFirst(str, separator) {
  const idx = str.indexOf(separator);
  if (idx === -1) return [str, undefined];
  return [str.slice(0, idx), str.slice(idx + 1)];
}

export function parseObsidianNote(rawText) {
  const text = typeof rawText === 'string' ? rawText : '';
  const { frontmatter, body } = extractFrontmatter(text);

  const scannable = maskCodeRegions(body);

  const frontmatterTags = frontmatter
    ? [...toTagList(frontmatter.tags), ...toTagList(frontmatter.tag)]
    : [];

  const inlineTags = [];
  for (const match of scannable.matchAll(INLINE_TAG_RE)) {
    const tag = match[1];
    if (/^[0-9]+$/.test(tag)) continue;
    inlineTags.push(tag);
  }

  const tags = Array.from(new Set([...frontmatterTags, ...inlineTags]));

  const wikilinks = [];
  const embeds = [];
  for (const match of scannable.matchAll(EMBED_OR_WIKILINK_RE)) {
    const isEmbed = match[1] === '!';
    const inner = match[2];
    const [beforePipe, aliasRaw] = splitFirst(inner, '|');
    const [targetRaw, headingRaw] = splitFirst(beforePipe, '#');
    const target = targetRaw.trim();

    if (isEmbed) {
      embeds.push({ target, raw: match[0] });
    } else {
      wikilinks.push({
        target,
        alias: aliasRaw !== undefined ? aliasRaw.trim() : null,
        heading: headingRaw !== undefined ? headingRaw.trim() : null,
        raw: match[0]
      });
    }
  }

  const links = [];
  for (const match of scannable.matchAll(MARKDOWN_LINK_RE)) {
    links.push({ text: match[1], url: match[2] });
  }

  return { body, frontmatter, tags, wikilinks, embeds, links };
}
